import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useWishlist } from '../contexts/WishlistContext';
import { Heart, Star } from 'lucide-react';
import { toast } from 'sonner';

export default function ProductCard({ product }) {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const name = lang === 'id' ? product.name_id : product.name_en;
  const desc = lang === 'id' ? product.description_id : product.description_en;
  const formatPrice = (price) => new Intl.NumberFormat('id-ID').format(price);
  const wishlisted = isWishlisted(product.product_id);

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.info(lang === 'id' ? 'Masuk untuk menyimpan favorit' : 'Sign in to save favorites'); return; }
    const added = await toggleWishlist(product.product_id);
    toast.success(added ? t('wishlist.added') : t('wishlist.removed'));
  };

  return (
    <Link
      to={`/product/${product.product_id}`}
      className="group block product-card-hover"
      data-testid={`product-card-${product.product_id}`}
    >
      <div className="glass-card rounded-3xl overflow-hidden">
        <div className="relative aspect-[4/5] overflow-hidden">
          <img
            src={product.images?.[0] || 'https://images.unsplash.com/photo-1551468220-0a25172193f9?w=400'}
            alt={name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2C2A29]/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          {/* Memory badge */}
          {product.is_memory_bouquet && (
            <div className="absolute top-3 left-3">
              <span className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-semibold glass-card-strong rounded-full text-[#D4859B]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#DDA7A5] animate-pulse" />
                Memory
              </span>
            </div>
          )}
          {/* Wishlist heart */}
          <button
            onClick={handleWishlist}
            className="absolute top-3 right-3 w-9 h-9 rounded-full glass-card-strong flex items-center justify-center transition-all duration-300 hover:scale-110 z-10"
            data-testid={`wishlist-btn-${product.product_id}`}
          >
            <Heart
              size={16}
              className={wishlisted ? 'text-[#DDA7A5] fill-[#DDA7A5]' : 'text-[#6B655F]'}
              strokeWidth={1.5}
            />
          </button>
          {/* Quick view hint */}
          <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-400">
            <div className="glass-card-strong rounded-2xl px-4 py-2.5 text-center">
              <span className="text-xs font-medium text-[#2C2A29] tracking-wide">{t('catalog.view_details')}</span>
            </div>
          </div>
        </div>
        <div className="p-5">
          <h3 className="font-['Cormorant_Garamond'] text-lg font-medium text-[#2C2A29] mb-1 line-clamp-1 group-hover:text-[#D4859B] transition-colors">
            {name}
          </h3>
          <p className="text-xs text-[#6B655F] font-light line-clamp-2 mb-3 leading-relaxed">
            {desc}
          </p>
          <div className="flex items-center justify-between">
            <span className="text-base font-semibold text-[#D4859B]">
              Rp {formatPrice(product.price)}
            </span>
            <div className="flex items-center gap-2">
              {product.avg_rating > 0 && (
                <span className="flex items-center gap-0.5 text-xs text-[#D4A853]">
                  <Star size={12} fill="#D4A853" strokeWidth={0} />
                  {product.avg_rating}
                </span>
              )}
              {product.style && (
                <span className="text-[10px] px-2.5 py-1 rounded-full bg-[#F3EEEA]/80 text-[#6B655F] capitalize font-medium">
                  {product.style}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
